import { StrictMode, Suspense, lazy, type ComponentType, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { EnvBanner } from '@qs/ui/EnvBanner';
import { Outlet, RouterProvider, createBrowserRouter } from 'react-router';
import { AuthProvider, RequireAuth } from './lib/auth';
import { AppError, installErrorReporting } from './lib/errors';
import './styles/global.css';

// 画面の種類ごとに分けて読み込む（参加者のスマホに主催者用の JS を送らない）
function page<M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M) {
  const C = lazy(async () => ({ default: (await load())[name] as ComponentType }));
  return <C />;
}
const auth = () => import('./pages/auth/AuthPages');
const host = () => import('./pages/host');
const dev = () => import('./pages/dev');
const participant = () => import('./pages/participant/Participant');
const remote = () => import('./pages/remote/Remote');
const projector = () => import('./pages/projector/Projector');
const consolePage = () => import('./pages/host/Console');

function Loading() {
  return (
    <div role="status" style={{ padding: 32, color: 'var(--color-ink-muted)' }}>
      読み込み中…
    </div>
  );
}

function WithAuth({ children }: { children: ReactNode }) {
  return (
    <AuthProvider>
      <Suspense fallback={<Loading />}>{children}</Suspense>
    </AuthProvider>
  );
}

function NotFound() {
  return (
    <div style={{ padding: 48, display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'flex-start' }}>
      <h1 style={{ font: 'var(--type-heading-1)', margin: 0 }}>ページが見つかりません</h1>
      <a href="/">ダッシュボードへ戻る</a>
    </div>
  );
}

installErrorReporting();

const router = createBrowserRouter([
  // 参加者・リモコン・投影：ログインの確認をしない（参加者用の JS を軽くする）
  {
    errorElement: <AppError />,
    element: (
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      { path: '/j/:code', element: page(participant, 'ParticipantPage') },
      { path: '/r/:code', element: page(remote, 'RemotePage') },
      { path: '/tournaments/:id/screen', element: page(projector, 'ProjectorPage') },
    ],
  },
  {
    errorElement: <AppError />,
    element: (
      <WithAuth>
        <Outlet />
      </WithAuth>
    ),
    children: [
      { path: '/login', element: page(auth, 'LoginPage') },
      { path: '/signup', element: page(auth, 'SignupPage') },
      { path: '/signup/sent', element: page(auth, 'SignupSentPage') },
      { path: '/verify', element: page(auth, 'VerifyPage') },
      { path: '/unsubscribe', element: page(auth, 'UnsubscribePage') },
      { path: '/password/forgot', element: page(auth, 'ForgotPage') },
      { path: '/password/reset', element: page(auth, 'ResetPage') },
      { path: '/', element: <RequireAuth>{page(host, 'Dashboard')}</RequireAuth> },
      { path: '/tournaments/:id/setup/:step', element: <RequireAuth>{page(host, 'SetupPage')}</RequireAuth> },
      { path: '/tournaments/:id/results', element: <RequireAuth>{page(host, 'ResultsPage')}</RequireAuth> },
      { path: '/packs', element: <RequireAuth>{page(host, 'PacksPage')}</RequireAuth> },
      { path: '/store', element: <RequireAuth>{page(host, 'StorePage')}</RequireAuth> },
      { path: '/packs/unlocked/:orderId', element: <RequireAuth>{page(host, 'UnlockedPage')}</RequireAuth> },
      { path: '/packs/return', element: <RequireAuth>{page(host, 'PayReturnPage')}</RequireAuth> },
      { path: '/account', element: <RequireAuth>{page(host, 'AccountPage')}</RequireAuth> },
      { path: '/account/receipts/:id', element: <RequireAuth>{page(host, 'ReceiptPage')}</RequireAuth> },
      { path: '/dev/paypay', element: page(dev, 'DevPayPayPage') },
      { path: '/history', element: <RequireAuth>{page(host, 'HistoryPage')}</RequireAuth> },
      { path: '/tournaments/:id/console', element: <RequireAuth>{page(consolePage, 'ConsolePage')}</RequireAuth> },
      { path: '/dev/mail', element: page(dev, 'DevMailPage') },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <EnvBanner />
    <RouterProvider router={router} />
  </StrictMode>,
);
